import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Paper, Grid, Typography, Button, IconButton, InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import {
    setViewType
} from '../../features/events/eventsSlice';
import { setMapControl } from '../../features/map/mapSlice';

import Icon from '@mdi/react';
import { mdiAccountPlusOutline, mdiMagnify, mdiCloseCircle } from '@mdi/js';

import SubAccountCreate from './SubAccountCreate';
import { getSubAccounts, searchSubAccounts } from '../../features/subaccounts/subaccountSlice';

const useStyles = makeStyles(() => ({
    paper: {
        borderRadius: 14,
        padding: '14px 16px 10px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    title: {
        fontWeight: 600,
        color: '#0f172a',
    },
    addButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 10,
        padding: '4px 10px',
        border: '1px solid rgba(148, 163, 184, 0.6)',
        color: '#1e3a8a',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    searchField: {
        marginTop: 10,
        maxWidth: 320,
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 12,
            boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.35)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#38bdf8',
        },
        '& .MuiInputAdornment-root': {
            marginRight: 4,
        },
        '& .MuiIconButton-root': {
            padding: 6,
        },
    },
}));

const SubAccountTop = () => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [subAccountFormOpen, setSubAccountFormOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const handleSubAccountFormOpen = () => {
        setSubAccountFormOpen(true);
    };
    const handleSubAccountFormClose = () => {
        setSubAccountFormOpen(false);
    }

    useEffect(() => {
        dispatch(setViewType('main'));
        dispatch(setMapControl('subaccount'));
    }, [dispatch]);

    const handleSearch = () => {
        const trimmed = searchValue.trim();
        if (!trimmed) {
            dispatch(getSubAccounts());
            return;
        }
        dispatch(searchSubAccounts({ query: trimmed }));
    };

    const handleClearSearch = () => {
        setSearchValue('');
        dispatch(getSubAccounts());
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    return (
        <Paper className={classes.paper}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <div className={classes.headerRow}>
                        <Typography variant="h6" className={classes.title}>
                            Sub Accounts
                        </Typography>
                        <Button
                            onClick={handleSubAccountFormOpen}
                            size="small"
                            className={classes.addButton}
                            startIcon={
                                <Icon
                                    path={mdiAccountPlusOutline}
                                    title="Create"
                                    size={0.8}
                                    color="#4338ca"
                                />
                            }
                        >
                            Add
                        </Button>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        label="Search"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search name, phone, username, or company"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSearch}
                                        size="small"
                                        aria-label="Search"
                                    >
                                        <Icon path={mdiMagnify} size={0.8} color="#334155" />
                                    </IconButton>
                                    {searchValue && (
                                        <IconButton
                                            onClick={handleClearSearch}
                                            size="small"
                                            aria-label="Clear search"
                                        >
                                            <Icon path={mdiCloseCircle} size={0.8} color="#ef4444" />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>
            <SubAccountCreate
                subAccountFormOpen={subAccountFormOpen}
                handleSubAccountFormClose={handleSubAccountFormClose}
            />
        </Paper>
    )
}

export default SubAccountTop;
